/**
 * API Client Service
 * 
 * Centralized HTTP client for making API requests.
 * Provides type-safe methods for all API interactions.
 */

import { buildApiUrl } from '../config/environment';
import type { ApiErrorResponse, RequestConfig } from '../types/api.types';

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Default request configuration
 */
const defaultConfig: RequestConfig = {
  method: 'GET',
  headers: {
    'Content-Type': 'application/json',
  },
  credentials: 'same-origin',
};

/**
 * Create fetch request with timeout
 */
const fetchWithTimeout = async (
  url: string,
  options: RequestInit,
  timeout: number = 10000
): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError('Request timeout', 408, 'TIMEOUT');
    }
    throw error;
  }
};

/**
 * Check if response is a Cloudflare Access redirect
 */
const isCloudflareAccessRedirect = (response: Response, url: string): boolean => {
  // Check if response was redirected
  if (response.redirected) {
    return true;
  }

  // Check if URL contains Cloudflare Access login path
  if (url.includes('/cdn-cgi/access/login') || response.url.includes('/cdn-cgi/access/login')) {
    return true;
  }

  // Check if content-type is HTML when we expected JSON (Access login page)
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('text/html') && !contentType.includes('application/json')) {
    return true;
  }

  return false;
};

/**
 * Parse JSON response with error handling
 */
const parseJsonResponse = async <T>(response: Response): Promise<T> => {
  const contentType = response.headers.get('content-type');
  
  // Check for Cloudflare Access redirect (HTML login page)
  if (contentType && contentType.includes('text/html') && !contentType.includes('application/json')) {
    throw new ApiError(
      'Unauthorized - Cloudflare Access required',
      401,
      'CLOUDFLARE_ACCESS_REDIRECT'
    );
  }
  
  if (!contentType || !contentType.includes('application/json')) {
    throw new ApiError(
      'Invalid response format',
      response.status,
      'INVALID_CONTENT_TYPE'
    );
  }

  try {
    return await response.json();
  } catch (error) {
    throw new ApiError(
      'Failed to parse response',
      response.status,
      'PARSE_ERROR'
    );
  }
};

/**
 * Handle API error responses
 */
const handleErrorResponse = async (response: Response): Promise<never> => {
  let errorData: ApiErrorResponse;
  
  try {
    errorData = await parseJsonResponse<ApiErrorResponse>(response);
  } catch {
    errorData = {
      message: `HTTP ${response.status}: ${response.statusText}`,
      status: response.status,
    };
  }

  throw new ApiError(
    errorData.message,
    errorData.status,
    errorData.code,
    errorData.details
  );
};

/**
 * Make an API request
 */
export const apiRequest = async <T>(
  endpoint: string,
  requestConfig: RequestConfig = {}
): Promise<T> => {
  const url = buildApiUrl(endpoint);
  const config = { ...defaultConfig, ...requestConfig };
  
  const requestInit: RequestInit = {
    method: config.method || 'GET',
    headers: {
      ...defaultConfig.headers,
      ...config.headers,
    },
    credentials: config.credentials,
    // CRITICAL: Don't follow redirects - Cloudflare Access redirects should be detected, not followed
    cache: 'no-store',
    redirect: 'manual' as RequestRedirect,
  };

  // Add body for non-GET requests
  if (config.body && config.method !== 'GET') {
    requestInit.body = JSON.stringify(config.body);
  }

  try {
    const response = await fetchWithTimeout(
      url,
      requestInit,
      config.timeout
    );

    // CRITICAL: Check for redirects BEFORE checking response.ok
    // With redirect: 'manual', redirect status codes (302, 301, etc.) are preserved
    // Cloudflare Access uses 302 redirects
    
    // Check for redirect status codes FIRST (302, 301, 307, 308)
    // This is the most reliable check with redirect: 'manual'
    if (response.status >= 300 && response.status < 400) {
      // This is a redirect - Cloudflare Access is blocking
      throw new ApiError(
        'Unauthorized - Cloudflare Access required',
        401,
        'CLOUDFLARE_ACCESS_REDIRECT'
      );
    }

    // Check response.type for opaqueredirect (cross-origin redirects)
    if (response.type === 'opaqueredirect') {
      throw new ApiError(
        'Unauthorized - Cloudflare Access required',
        401,
        'CLOUDFLARE_ACCESS_REDIRECT'
      );
    }

    // Check for status 0 (some browsers return this for certain redirects)
    if (response.status === 0) {
      throw new ApiError(
        'Unauthorized - Cloudflare Access required',
        401,
        'CLOUDFLARE_ACCESS_REDIRECT'
      );
    }

    // Check if response was redirected (shouldn't happen with redirect: 'manual', but safety check)
    if (response.redirected) {
      throw new ApiError(
        'Unauthorized - Cloudflare Access required',
        401,
        'CLOUDFLARE_ACCESS_REDIRECT'
      );
    }

    // Check if response URL changed to Cloudflare Access login page
    // This catches cases where redirect happened despite redirect: 'manual'
    if (response.url && response.url !== url && response.url.includes('/cdn-cgi/access/login')) {
      throw new ApiError(
        'Unauthorized - Cloudflare Access required',
        401,
        'CLOUDFLARE_ACCESS_REDIRECT'
      );
    }

    // Check for Cloudflare Access redirect by URL or content type
    // This catches cases where Access returns 200 with HTML login page
    if (isCloudflareAccessRedirect(response, url)) {
      throw new ApiError(
        'Unauthorized - Cloudflare Access required',
        401,
        'CLOUDFLARE_ACCESS_REDIRECT'
      );
    }

    // Now check for other error status codes
    if (!response.ok) {
      await handleErrorResponse(response);
    }

    return await parseJsonResponse<T>(response);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Network or other errors
    throw new ApiError(
      error instanceof Error ? error.message : 'Network error occurred',
      0,
      'NETWORK_ERROR'
    );
  }
};

/**
 * GET request helper
 */
export const apiGet = <T>(
  endpoint: string,
  config?: { 
    headers?: Record<string, string>;
    credentials?: RequestCredentials;
    timeout?: number;
  }
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    method: 'GET',
    headers: config?.headers,
    credentials: config?.credentials,
    timeout: config?.timeout,
  });
};

/**
 * POST request helper
 */
export const apiPost = <T>(
  endpoint: string,
  body: unknown,
  config?: { headers?: Record<string, string> }
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    method: 'POST',
    body,
    headers: config?.headers,
  });
};

/**
 * PUT request helper
 */
export const apiPut = <T>(
  endpoint: string,
  body: unknown,
  config?: { headers?: Record<string, string> }
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    method: 'PUT',
    body,
    headers: config?.headers,
  });
};

/**
 * DELETE request helper
 */
export const apiDelete = <T>(
  endpoint: string,
  config?: { headers?: Record<string, string> }
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    method: 'DELETE',
    headers: config?.headers,
  });
};

